// Portal de armerías (/armeros) → pestaña «Mi cuenta»: balance por mes, pago
// por ACH, cuenta bancaria (Stripe) y débito automático opcional. Todo pasa
// por las functions partner* (functions/partnersAch.js); el portal nunca lee
// partner_private ni ids de Stripe.
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import axios from 'axios';
import Iconify from '../../components/iconify';
import {
  money,
  monthLabel,
  settlementStatus,
  formatDueDate,
  BANK_STATUS,
  TERMS_PDF_PATH,
} from '../../utils/partners';

const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';
const STRIPE_PK = 'pk_live_51Q5pO5RqULsyaFnGO4c7JjHvqYv9Eu4FovYOlgCfuWZyrpYgn33p1cZavi28z41Nxpo3PiKLbscqL7c2ezQUFux400HaYeh7o6';
const RED_BUTTON = { bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' } };
const apiError = (err, fallback) => (err.response && err.response.data && err.response.data.error) || fallback;

let stripePromise = null;
const getStripePromise = () => {
  if (!stripePromise) stripePromise = loadStripe(STRIPE_PK);
  return stripePromise;
};

// Llama a una function partner* con las credenciales de la sesión
export async function partnerApi(partner, name, body = {}) {
  const res = await axios.post(`${FUNCTIONS_BASE}/${name}`, { partnerId: partner.id, partnerPin: partner.pin, ...body });
  return res.data;
}

// ---------------------------------------------------------------------------
// Aceptación de términos (bloquea el portal hasta aceptar la versión vigente)
// ---------------------------------------------------------------------------
export function TermsDialog({ open, partner, account, onAccepted, onLogout }) {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isUpdate = Boolean(account && account.termsAcceptedAt);

  const accept = async () => {
    setSaving(true);
    setError('');
    try {
      const data = await partnerApi(partner, 'partnerAcceptTerms');
      onAccepted(data);
    } catch (err) {
      setError(apiError(err, 'No se pudo guardar la aceptación.'));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>{isUpdate ? 'Actualizamos los términos del programa' : 'Términos del Programa de Armerías Aliadas'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Para seguir inscribiendo clientes, <strong>{partner.name}</strong> debe aceptar los términos y condiciones del programa. Lo más importante:
        </Typography>
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="body2">• Cobras el curso en tu negocio; BBTC te factura los cursos a los que tus clientes <strong>asistieron</strong>, menos la comisión por referido.</Typography>
          <Typography variant="body2">• Cada inasistencia o cancelación con menos de 24 horas tiene una <strong>penalidad de $20</strong> (sin comisión).</Typography>
          <Typography variant="body2">• El estado de cuenta se cierra el <strong>último día de cada mes</strong> y se paga en su totalidad <strong>dentro de los 5 días siguientes</strong> (a más tardar el día 5).</Typography>
          <Typography variant="body2">• Puedes pagar por ACH desde este portal o activar el débito automático. No hay intereses ni cargos por mora; un balance vencido puede suspender nuevas inscripciones.</Typography>
        </Stack>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Lee el documento completo: <Link href={TERMS_PDF_PATH} target="_blank" rel="noopener">Términos y condiciones (PDF)</Link>.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControlLabel
          control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
          label="He leído y acepto los términos y condiciones en nombre de la armería"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onLogout} disabled={saving}>Salir</Button>
        <Button variant="contained" onClick={accept} disabled={!checked || saving} sx={RED_BUTTON}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Aceptar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TermsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  partner: PropTypes.object.isRequired,
  account: PropTypes.object,
  onAccepted: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Vincular cuenta bancaria (Stripe Elements, solo us_bank_account)
// ---------------------------------------------------------------------------
function BankForm({ partner, onLinked, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!stripe || !elements) return;
    setSaving(true);
    setError('');
    try {
      const { error: subErr } = await elements.submit();
      if (subErr) { setError(subErr.message); setSaving(false); return; }
      const result = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: { return_url: `${window.location.origin}${window.location.pathname}?tab=cuenta` },
      });
      if (result.error) { setError(result.error.message); setSaving(false); return; }
      const data = await partnerApi(partner, 'partnerConfirmBank', { setupIntentId: result.setupIntent.id });
      onLinked(data);
    } catch (err) {
      setError(apiError(err, 'No se pudo vincular la cuenta.'));
    }
    setSaving(false);
  };

  return (
    <>
      <PaymentElement
        options={{
          business: { name: 'B&B Target Center' },
          defaultValues: { billingDetails: { name: partner.contactName || partner.name, email: partner.email || '' } },
        }}
      />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
        <Button onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !stripe} sx={RED_BUTTON}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Vincular cuenta'}
        </Button>
      </Stack>
    </>
  );
}

BankForm.propTypes = {
  partner: PropTypes.object.isRequired,
  onLinked: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

function BankLinkDialog({ open, partner, onClose, onLinked }) {
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) { setClientSecret(''); setError(''); return; }
    partnerApi(partner, 'partnerCreateBankSetupIntent')
      .then((d) => setClientSecret(d.clientSecret))
      .catch((err) => setError(apiError(err, 'No se pudo iniciar la vinculación.')));
  }, [open, partner]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Vincular cuenta bancaria</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Entra a tu banco para conectar la cuenta al instante. Si tu banco no aparece, escoge «Enter bank details manually»: Stripe hará dos microdepósitos y tendrás que confirmarlos en 1 a 2 días.
          Los pagos ACH cuestan mucho menos que una tarjeta y aparecen como <strong>B&B TARGET CENTER</strong> en tu estado bancario.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!clientSecret && !error && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
        {clientSecret && (
          <Elements stripe={getStripePromise()} options={{ clientSecret, locale: 'es', appearance: { variables: { colorPrimary: '#d40100' } } }}>
            <BankForm partner={partner} onLinked={onLinked} onCancel={onClose} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}

BankLinkDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  partner: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onLinked: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Pestaña «Mi cuenta»
// ---------------------------------------------------------------------------
export default function PartnerAccountTab({ partner, account, loading, onAccount, onReload }) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [autoDialog, setAutoDialog] = useState(false);
  const [autoChecked, setAutoChecked] = useState(false);

  const call = async (name, body, label, successMsg) => {
    setBusy(label);
    setError('');
    setNotice('');
    try {
      const data = await partnerApi(partner, name, body);
      onAccount(data.account || data);
      if (successMsg) setNotice(successMsg);
      return data;
    } catch (err) {
      setError(apiError(err, 'No se pudo completar la acción.'));
      return null;
    } finally {
      setBusy('');
    }
  };

  if (loading && !account) return <Card sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}><CircularProgress /></Card>;
  if (!account) {
    return (
      <Card sx={{ p: 3, borderRadius: 3 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={onReload}>Reintentar</Button>}>No se pudo cargar tu cuenta.</Alert>
      </Card>
    );
  }

  const { bank } = account;
  const bankVerified = Boolean(bank && bank.status === 'verified');
  const bankSt = bank ? (BANK_STATUS[bank.status] || { label: bank.status, color: 'default' }) : null;
  const pending = account.settlements.filter((s) => ['closed', 'failed', 'processing', 'disputed'].includes(s.status));
  const history = account.settlements.filter((s) => ['paid', 'waived'].includes(s.status));

  const doPay = async () => {
    const row = payRow;
    setPayRow(null);
    const data = await call('partnerPayStatement', { settlementId: row.id }, `pay-${row.id}`, `Iniciamos el débito de ${money(row.amountDueCents)} por ${monthLabel(row.month)}. El banco lo confirma en 3 a 5 días hábiles; te avisaremos por email.`);
    if (data) onReload();
  };

  const setAuto = async (enabled) => {
    setAutoDialog(false);
    setAutoChecked(false);
    await call('partnerSetAutoDebit', { enabled }, 'auto', enabled ? 'Débito automático activado: el balance se debita el día de su vencimiento.' : 'Débito automático desactivado.');
  };

  return (
    <Stack spacing={2}>
      {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {account.suspendedForNonPayment && (
        <Alert severity="error">Tus inscripciones están <strong>suspendidas por balance vencido</strong>. Paga el balance pendiente o comunícate con BBTC para reactivarlas.</Alert>
      )}

      {/* Balance */}
      <Card sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">Balance con BBTC</Typography>
            <Typography variant="body2" color="text.secondary">
              El mes cierra el último día y se paga dentro de los {account.paymentTermDays} días siguientes. El mes en curso lo ves en «Mis clientes».
            </Typography>
          </Box>
          <Box sx={{ textAlign: { sm: 'right' } }}>
            <Typography variant="h4" fontWeight="bold" color={account.overdueCents > 0 ? 'error.main' : 'text.primary'}>{money(account.balanceDueCents)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {account.overdueCents > 0 ? `${money(account.overdueCents)} vencido` : (account.processingCents > 0 ? `${money(account.processingCents)} en proceso` : 'pendiente de pago')}
            </Typography>
          </Box>
        </Stack>

        {pending.length === 0 && <Typography color="text.secondary">No tienes estados de cuenta pendientes.</Typography>}
        {/* Celular: una tarjeta por mes (la tabla no cabe) */}
        {pending.length > 0 && (
          <Stack spacing={1.5} sx={{ display: { xs: 'flex', sm: 'none' } }}>
            {pending.map((s) => {
              const st = settlementStatus(s, account.today);
              const payable = ['closed', 'failed'].includes(s.status) && s.amountDueCents > 0;
              return (
                <Card key={s.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>{monthLabel(s.month)}</Typography>
                      <Typography variant="caption" color="text.secondary">Vence el {formatDueDate(s.dueDate)}</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>{money(s.amountDueCents)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                    <Chip size="small" label={st.label} color={st.color} variant={st.overdue ? 'filled' : 'outlined'} />
                    {payable && (
                      <Button size="small" variant="contained" sx={RED_BUTTON} disabled={!bankVerified || Boolean(busy)} onClick={() => setPayRow(s)}>Pagar por ACH</Button>
                    )}
                  </Stack>
                  {s.status === 'failed' && s.lastChargeError && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>{s.lastChargeError}</Typography>}
                </Card>
              );
            })}
          </Stack>
        )}
        {pending.length > 0 && (
          <TableContainer sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mes</TableCell>
                  <TableCell>Vence</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.map((s) => {
                  const st = settlementStatus(s, account.today);
                  const payable = ['closed', 'failed'].includes(s.status) && s.amountDueCents > 0;
                  return (
                    <TableRow key={s.id}>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{monthLabel(s.month)}</TableCell>
                      <TableCell>{formatDueDate(s.dueDate)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={st.label} color={st.color} variant={st.overdue ? 'filled' : 'outlined'} />
                        {s.status === 'failed' && s.lastChargeError && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{s.lastChargeError}</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{money(s.amountDueCents)}</TableCell>
                      <TableCell align="right">
                        {payable && (
                          <Button size="small" variant="contained" sx={RED_BUTTON} disabled={!bankVerified || Boolean(busy)} onClick={() => setPayRow(s)}>
                            Pagar por ACH
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {pending.some((s) => ['closed', 'failed'].includes(s.status)) && !bankVerified && (
          <Alert severity="info" sx={{ mt: 2 }}>Para pagar por ACH primero vincula tu cuenta bancaria (abajo). También puedes pagar por otro medio y enviar el comprobante a BBTC.</Alert>
        )}
      </Card>

      {/* Cuenta bancaria */}
      <Card sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>Cuenta bancaria (ACH)</Typography>
        {!bank && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Vincula la cuenta de cheques de la armería para pagar tus estados de cuenta por ACH con un clic, o activar el débito automático.
            </Typography>
            <Button variant="contained" sx={RED_BUTTON} startIcon={<Iconify icon="eva:link-2-fill" />} onClick={() => setLinkOpen(true)}>Vincular cuenta bancaria</Button>
          </>
        )}
        {bank && (
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
              <Iconify icon="mdi:bank-outline" width={28} />
              <Typography variant="subtitle1">{bank.bankName || 'Banco'} ••••{bank.last4}</Typography>
              <Chip size="small" label={bankSt.label} color={bankSt.color} />
            </Stack>
            {bank.status === 'pending_verification' && (
              <Alert severity="warning">
                Stripe hizo dos microdepósitos en tu cuenta. Cuando los veas (1 a 2 días), confírmalos aquí:
                {' '}
                {bank.hostedVerificationUrl ? <Link href={bank.hostedVerificationUrl} target="_blank" rel="noopener">verificar microdepósitos</Link> : 'revisa el email de Stripe'}.
              </Alert>
            )}
            {bank.status === 'errored' && <Alert severity="error">La verificación falló. Quita esta cuenta y vincúlala de nuevo.</Alert>}
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setLinkOpen(true)} disabled={Boolean(busy)}>Cambiar cuenta</Button>
              <Button size="small" color="error" disabled={Boolean(busy)} onClick={() => { if (window.confirm('¿Quitar la cuenta bancaria? Se desactiva el débito automático.')) call('partnerRemoveBank', {}, 'remove', 'Cuenta bancaria eliminada.'); }}>Quitar</Button>
            </Stack>
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="subtitle1">Débito automático</Typography>
            <Typography variant="body2" color="text.secondary">
              Opcional. Si lo activas, el balance de cada mes se debita solo el día de su vencimiento (el {account.paymentTermDays} del mes siguiente). Lo puedes desactivar cuando quieras.
            </Typography>
            {account.autoDebit && account.autoDebitAcceptedAt && (
              <Typography variant="caption" color="text.secondary">Activado el {new Date(account.autoDebitAcceptedAt).toLocaleDateString('es-PR')}.</Typography>
            )}
          </Box>
          <Switch
            checked={account.autoDebit}
            disabled={!bankVerified || Boolean(busy)}
            onChange={(e) => { if (e.target.checked) { setAutoChecked(false); setAutoDialog(true); } else setAuto(false); }}
          />
        </Stack>
        {!bankVerified && <Typography variant="caption" color="text.secondary">Para activarlo necesitas una cuenta bancaria verificada.</Typography>}
      </Card>

      {/* Historial */}
      {history.length > 0 && (
        <Card sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>Meses pagados</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Mes</TableCell><TableCell>Estado</TableCell><TableCell>Pagado</TableCell><TableCell align="right">Total</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {history.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{monthLabel(s.month)}</TableCell>
                    <TableCell><Chip size="small" label={settlementStatus(s).label} color={settlementStatus(s).color} variant="outlined" /></TableCell>
                    <TableCell>{s.paidAt ? new Date(s.paidAt).toLocaleDateString('es-PR') : '—'}{s.paidVia === 'ach' ? ' · ACH' : ''}</TableCell>
                    <TableCell align="right">{money(s.totalCents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Typography variant="caption" color="text.secondary">
        Términos aceptados {account.termsAcceptedAt ? `el ${new Date(account.termsAcceptedAt).toLocaleDateString('es-PR')}` : '—'} ·{' '}
        <Link href={TERMS_PDF_PATH} target="_blank" rel="noopener">ver términos (PDF)</Link>
      </Typography>

      <BankLinkDialog open={linkOpen} partner={partner} onClose={() => setLinkOpen(false)} onLinked={(data) => { setLinkOpen(false); onAccount(data); setNotice(data.bank && data.bank.status === 'verified' ? 'Cuenta bancaria vinculada y verificada.' : 'Cuenta vinculada. Confirma los microdepósitos cuando los veas en tu banco.'); }} />

      <Dialog open={Boolean(payRow)} onClose={() => setPayRow(null)} fullWidth maxWidth="xs">
        <DialogTitle>Pagar por ACH</DialogTitle>
        <DialogContent>
          {payRow && (
            <Typography variant="body2">
              Se debitarán <strong>{money(payRow.amountDueCents)}</strong> de {bank ? `${bank.bankName || 'tu cuenta'} ••••${bank.last4}` : 'tu cuenta'} por el estado de cuenta de <strong>{monthLabel(payRow.month)}</strong>.
              El banco tarda de 3 a 5 días hábiles en confirmarlo.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayRow(null)}>Cancelar</Button>
          <Button variant="contained" sx={RED_BUTTON} onClick={doPay}>Confirmar pago</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={autoDialog} onClose={() => setAutoDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Activar débito automático</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>{account.autoDebitMandateText}</Alert>
          <FormControlLabel control={<Checkbox checked={autoChecked} onChange={(e) => setAutoChecked(e.target.checked)} />} label="Autorizo el débito automático en los términos anteriores" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoDialog(false)}>Cancelar</Button>
          <Button variant="contained" sx={RED_BUTTON} disabled={!autoChecked} onClick={() => setAuto(true)}>Activar</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

PartnerAccountTab.propTypes = {
  partner: PropTypes.object.isRequired,
  account: PropTypes.object,
  loading: PropTypes.bool,
  onAccount: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
};
